import { Grid } from "@mui/material";
import SoftBox from "components/SoftBox";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import ReactCropModal from "../../ReactCropModal/ReactCropModal";
import HeaderContentSection from "./HeaderContentSection";
import HeroContentSection from "./HeroContentSection";
import HomePageHeadingsSection from "./HomePageHeadingsSection";
import HomePageHeadingsStylingSection from "./HomePageHeadingsStylingSection";
import HomepageOptinSection from "./HomepageOptinSection";
import PlaceholderCard from "./PlaceholderCard";
import FooterContentSection from "./FooterContentSection";
import AcknowledgmentContentSection from "./AcknowledgmentContentSection";
import SocialLinksSection from "./SocialLinksSection";
import CustomerInfoSection from "./CustomerInfoSection";
import FooterStylingSection from "./FooterStylingSection";
import ButtonStylingSection from "./ButtonStylingSection";
import useImageUpload from "./hooks/useImageUpload";
import useAcknowledgments from "./hooks/useAcknowledgments";
import useTagDropdown from "./hooks/useTagDropdown";
import toast from "react-hot-toast";
import A2PApprovalSection from "./A2PApprovalSection";
import GhlMediaGallery from "components/GhlMediaGallery";

const FIELD_LABELS = {
  // Header
  email: "Email",
  phone: "Phone",
  // Hero
  main_image: "Hero image",
  title: "Hero title",
  sub_title: "Hero subtitle",
  hero_text_heading: "Hero heading color",
  hero_text_sub_heading: "Hero subheading color",
  hero_text_heading_size: "Hero heading size",
  hero_text_sub_heading_size: "Hero subheading size",
  hero_section_image_height: "Hero image height",
  search_size: "Search bar size",
  home_hero_search_placeholder: "Search placeholder",
  showMap: "Display Map",
  showMapPins: "Map business pins",
  homepageMapPinLimit: "Homepage map pin limit",
  homepageMapZoomBias: "Homepage map zoom",
  // Home headings
  img_1: "Home page image",
  heading_1: "Home heading",
  text_1: "Home text 1",
  text_2: "Home text 2",
  featured_listings_headline: "Featured Listings Headline",
  listings_headline: "Listings Headline",
  listing_labels_headline: "Listing Labels Headline",
  // Homepage Optin
  optin_image: "Optin background image",
  optin_heading: "Optin headline",
  optin_sub_heading: "Optin sub-headline",
  optin_form_id: "Optin connect form",
  optin_form_name: "Optin connect form",
  optin_form_embed_code: "Optin form embed code",
  // Home headings styling
  heading_1_color: "Home heading color",
  text_1_color: "Home text 1 color",
  text_2_color: "Home text 2 color",
  heading_1_font: "Home heading font",
  text_1_font: "Home text 1 font",
  text_2_font: "Home text 2 font",
  heading_1_font_size: "Home heading font size",
  text_1_font_size: "Home text 1 font size",
  text_2_font_size: "Home text 2 font size",
  // Listings + badge
  featured_listings_per_page: "Featured listings per page",
  free_listings_per_page: "Free listings per page",
  directory_listings_per_page: "Category listings per page",
  showPremiumListingBadge: "Featured listing badge",
  showClaimedListingBadge: "Claimed listing badge",
  premium_listing_badge_label: "Premium badge text",
  premium_listing_badge_bg: "Premium badge background",
  premium_listing_badge_text_color: "Premium badge text color",
  premium_listing_badge_icon: "Premium badge icon",
  claimed_listing_badge_label: "Claimed badge text",
  claimed_listing_badge_bg: "Claimed badge background",
  claimed_listing_badge_text_color: "Claimed badge text color",
  claimed_listing_badge_icon: "Claimed badge icon",
  // Social
  facebook: "Facebook link",
  instagram: "Instagram link",
  linkedin: "LinkedIn link",
  twitter: "Twitter link",
  youtube: "Youtube link",
  contact_us: "Contact Us link",
  learn_more: "Learn More link",
  // Customer info
  name: "Name",
  phone_number: "Phone number",
  // Footer
  footer_layout: "Footer layout",
  address: "Address",
  copyright: "Copyright",
  footer_phone: "Footer Phone",
  footer_email: "Footer Email",
  footer_address: "Footer Address",
  footer_follow_us: "Footer Follow Us on Social Media",
  footer_background: "Footer background",
  footer_text: "Footer text color",
  footer_font: "Footer font",
  footer_font_size: "Footer font size",
  // Button
  button_bg: "Button background",
  button_text: "Button text color",
  button_border: "Button border color",
  button_font: "Button font",
  button_font_size: "Button font size",
  // Acknowledgments
  acknowledgments: "Acknowledgments",
};

const labelFor = (name) => FIELD_LABELS[name] || name;

const GENERAL_MIRROR_FIELDS = new Set([
  // social
  "facebook", "instagram", "linkedin", "twitter", "youtube", "contact_us", "learn_more",
  // customer info
  "email", "name", "phone_number",
  // footer styling
  "footer_font", "footer_font_size", "footer_background", "footer_text",
  // button styling
  "button_bg", "button_text", "button_border", "button_font", "button_font_size",
]);

const ALLOWED_BADGE_ICONS = new Set([
  "bolt", "star", "crown", "verified", "flame", "sparkle",
  "medal", "trophy", "gem", "pin", "bookmark", "none",
]);

const sanitizeFieldValue = (name, value) => {
  if (name === "premium_listing_badge_label" || name === "claimed_listing_badge_label") {
    return String(value ?? "").trim().slice(0, 20);
  }
  if (name === "home_hero_search_placeholder") {
    return String(value ?? "").trim().slice(0, 160);
  }
  if (name === "homepageMapZoomBias") {
    const n = Math.round(Number(value));
    if (!Number.isFinite(n)) return 0;
    return Math.min(2, Math.max(-2, n));
  }
  if (name === "footer_layout") {
    const v = String(value ?? "").trim().toLowerCase();
    return v === "modern" ? "modern" : "classic";
  }
  if (name === "premium_listing_badge_icon" || name === "claimed_listing_badge_icon") {
    const k = String(value ?? "").trim().toLowerCase();
    if (!ALLOWED_BADGE_ICONS.has(k)) {
      return name === "claimed_listing_badge_icon" ? "verified" : "bolt";
    }
    return k;
  }
  return value;
};

function Content({ handelThemeData, selectedTheme, buttonLoading }) {
  const contentData = selectedTheme?.theme_data?.content;
  const generalData = selectedTheme?.theme_data?.general;
  // Fallback to general data for social links and customer info if not in content (for migration)
  const inputData = {
    ...contentData,
    facebook: contentData?.facebook || generalData?.facebook,
    instagram: contentData?.instagram || generalData?.instagram,
    linkedin: contentData?.linkedin || generalData?.linkedin,
    twitter: contentData?.twitter || generalData?.twitter,
    youtube: contentData?.youtube || generalData?.youtube,
    contact_us: contentData?.contact_us || generalData?.contact_us,
    learn_more: contentData?.learn_more || generalData?.learn_more,
    email: contentData?.email ?? generalData?.email ?? "",
    name: contentData?.name || generalData?.name,
    phone_number: contentData?.phone_number || generalData?.phone_number,
    phone: contentData?.phone ?? generalData?.phone ?? contentData?.phone_number ?? generalData?.phone_number ?? "",
    footer_font: contentData?.footer_font || generalData?.footer_font,
    footer_font_size: contentData?.footer_font_size || generalData?.footer_font_size,
    footer_background: contentData?.footer_background || generalData?.footer_background,
    footer_text: contentData?.footer_text || generalData?.footer_text,
    button_bg: contentData?.button_bg || generalData?.button_bg,
    button_text: contentData?.button_text || generalData?.button_text,
    button_border: contentData?.button_border || generalData?.button_border,
    button_font: contentData?.button_font || generalData?.button_font,
    button_font_size: contentData?.button_font_size || generalData?.button_font_size,
    featured_listings_per_page: contentData?.featured_listings_per_page || generalData?.featured_listings_per_page || "3",
    free_listings_per_page: contentData?.free_listings_per_page || generalData?.free_listings_per_page || "3",
    directory_listings_per_page:
      contentData?.directory_listings_per_page ||
      generalData?.directory_listings_per_page ||
      "6",
    showPremiumListingBadge: contentData?.showPremiumListingBadge !== false,
    showClaimedListingBadge: contentData?.showClaimedListingBadge !== false,
    premium_listing_badge_label: contentData?.premium_listing_badge_label ?? "",
    premium_listing_badge_bg: contentData?.premium_listing_badge_bg ?? "",
    premium_listing_badge_text_color: contentData?.premium_listing_badge_text_color ?? "",
    premium_listing_badge_icon: contentData?.premium_listing_badge_icon ?? "bolt",
    claimed_listing_badge_label: contentData?.claimed_listing_badge_label ?? "",
    claimed_listing_badge_bg: contentData?.claimed_listing_badge_bg ?? "",
    claimed_listing_badge_text_color: contentData?.claimed_listing_badge_text_color ?? "",
    claimed_listing_badge_icon: contentData?.claimed_listing_badge_icon ?? "verified",
  };
  const [agency] = useAgencyInfo();
  const services = useAppServices();

  const [showMap, setShowMap] = useState(inputData?.showMap || false);
  const [showMapPins, setShowMapPins] = useState(inputData?.showMapPins !== false);
  const [showPremiumListingBadge, setShowPremiumListingBadge] = useState(
    inputData?.showPremiumListingBadge !== false
  );
  const [showClaimedListingBadge, setShowClaimedListingBadge] = useState(
    inputData?.showClaimedListingBadge !== false
  );
  const [showLogin, setShowLogin] = useState(inputData?.showLogin !== false);
  const [showRegister, setShowRegister] = useState(inputData?.showRegister !== false);
  const [showAddNewBusiness, setShowAddNewBusiness] = useState(inputData?.showAddNewBusiness !== false);
  const [showFollow, setShowFollow] = useState(inputData?.showFollow !== false);
  const [showClaim, setShowClaim] = useState(inputData?.showClaim !== false);
  const [main_image, setMainImage] = useState(inputData?.main_image || "");
  const [img_1, setImg1] = useState(inputData?.img_1 || "");
  const [optin_image, setOptinImage] = useState(inputData?.optin_image || "");
  const [mainImageId, setMainImageId] = useState(inputData?.main_image_id || "");
  const [img1Id, setImg1Id] = useState(inputData?.img_1_id || "");
  const [optinImageId, setOptinImageId] = useState(inputData?.optin_image_id || "");
  const [previewMainImage, setPreviewMainImage] = useState(main_image);
  const [previewImg1, setPreviewImg1] = useState(img_1);
  const [previewOptinImage, setPreviewOptinImage] = useState(optin_image);
  const [headerEmail, setHeaderEmail] = useState(inputData?.email ?? "");
  const [headerPhone, setHeaderPhone] = useState(inputData?.phone ?? "");

  // Update previews when inputData changes
  useEffect(() => {
    if (inputData?.main_image) {
      setMainImage(inputData.main_image);
      setMainImageId(inputData.main_image_id);
      setPreviewMainImage(inputData.main_image);
    }
    if (inputData?.img_1) {
      setImg1(inputData.img_1);
      setImg1Id(inputData.img_1_id);
      setPreviewImg1(inputData.img_1);
    }
    if (inputData?.optin_image) {
      setOptinImage(inputData.optin_image);
      setOptinImageId(inputData.optin_image_id);
      setPreviewOptinImage(inputData.optin_image);
    }
    if (inputData?.showMap !== undefined) {
      setShowMap(inputData.showMap);
    }
    if (inputData?.showMapPins !== undefined) {
      setShowMapPins(inputData.showMapPins);
    }
    if (inputData?.showPremiumListingBadge !== undefined) {
      setShowPremiumListingBadge(inputData.showPremiumListingBadge !== false);
    }
    if (inputData?.showClaimedListingBadge !== undefined) {
      setShowClaimedListingBadge(inputData.showClaimedListingBadge !== false);
    }
    if (inputData?.showLogin !== undefined) {
      setShowLogin(inputData.showLogin);
    }
    if (inputData?.showRegister !== undefined) {
      setShowRegister(inputData.showRegister);
    }
    if (inputData?.showAddNewBusiness !== undefined) {
      setShowAddNewBusiness(inputData.showAddNewBusiness);
    }
  }, [inputData]);

  // Sync header email/phone only when theme data actually changes (load or after save); primitive deps avoid reset on every render
  useEffect(() => {
    setHeaderEmail(inputData?.email ?? "");
    setHeaderPhone(inputData?.phone ?? "");
  }, [inputData?.email, inputData?.phone]);

  const AUTO_SAVE_TOAST_ID = "branding-content-autosave";

  const runAutoSave = useCallback(
    (data, { loadingMsg = "Saving...", successMsg = "Saved", errorMsg } = {}) => {
      const promise = handelThemeData(data).then((result) => {
        if (result && result.success === false) {
          throw new Error(result.message || errorMsg || "Failed to save");
        }
        return result;
      });

      toast.promise(
        promise,
        {
          loading: loadingMsg,
          success: successMsg,
          error: (err) => err?.message || errorMsg || "Failed to save",
        },
        { id: AUTO_SAVE_TOAST_ID }
      );

      return promise;
    },
    [handelThemeData]
  );

  const persistedContentRef = useRef(contentData);
  const persistedGeneralRef = useRef(generalData);
  const persistedElementsRef = useRef(selectedTheme?.theme_data?.elements);
  useEffect(() => {
    persistedContentRef.current = contentData;
    persistedGeneralRef.current = generalData;
    persistedElementsRef.current = selectedTheme?.theme_data?.elements;
  }, [contentData, generalData, selectedTheme?.theme_data?.elements]);

  const autoSaveField = useCallback(
    (name, rawValue) => {
      if (!name) return;
      const value = sanitizeFieldValue(name, rawValue);
      const currentContent = persistedContentRef.current?.[name];
      const currentGeneral = persistedGeneralRef.current?.[name];
      const mirror = GENERAL_MIRROR_FIELDS.has(name);

      const contentSame = (currentContent ?? "") === (value ?? "");
      const generalSame = !mirror || (currentGeneral ?? "") === (value ?? "");
      if (contentSame && generalSame) return;

      const payload = { content: { [name]: value } };
      if (mirror) payload.general = { [name]: value };

      const label = labelFor(name);
      runAutoSave(payload, {
        loadingMsg: `Updating ${label}...`,
        successMsg: `${label} updated`,
        errorMsg: `Failed to update ${label}`,
      }).catch(() => {});
    },
    [runAutoSave]
  );

  // handelThemeData replaces `elements` shallowly, so we must spread existing keys.
  const autoSaveElement = useCallback(
    (name, rawValue) => {
      if (!name) return;
      const value = (rawValue ?? "").toString();
      const existingElements = persistedElementsRef.current || {};
      if ((existingElements[name] ?? "") === value) return;

      const merged = { ...existingElements };
      if (value.trim()) merged[name] = value;
      else delete merged[name];

      const label = labelFor(name);
      runAutoSave(
        { elements: merged },
        {
          loadingMsg: `Updating ${label}...`,
          successMsg: `${label} updated`,
          errorMsg: `Failed to update ${label}`,
        }
      ).catch(() => {});
    },
    [runAutoSave]
  );

  // Custom hooks - useMemo to prevent recreating object on every render
  // Note: setter functions from useState are stable, so we don't need them in deps
  const imageSetters = useMemo(() => ({
    main_image: { setImage: setMainImage, setPreview: setPreviewMainImage },
    img_1: { setImage: setImg1, setPreview: setPreviewImg1 },
    optin_image: { setImage: setOptinImage, setPreview: setPreviewOptinImage },
  }), []);

  const IMAGE_ID_FIELDS = {
    main_image: "main_image_id",
    img_1: "img_1_id",
    optin_image: "optin_image_id",
  };

  const handleImageUploadSuccess = useCallback((type, url, Id) => {
    const label = labelFor(type);
    const idField = IMAGE_ID_FIELDS[type];
    runAutoSave(
      { content: { [type]: url, [idField]: Id } },
      {
        loadingMsg: `Uploading ${label}...`,
        successMsg: `${label} uploaded`,
        errorMsg: `Failed to upload ${label}`,
      }
    ).catch(() => {});
  }, [runAutoSave]);

  const handleRemoveContentImage = useCallback(
    (type, imageId) => {
      if (type === "main_image") {
        setMainImage("");
        setMainImageId("");
        setPreviewMainImage("");
      } else if (type === "img_1") {
        setImg1("");
        setImg1Id("");
        setPreviewImg1("");
      } else if (type === "optin_image") {
        setOptinImage("");
        setOptinImageId("");
        setPreviewOptinImage("");
      } else {
        return;
      }
      const label = labelFor(type);
      runAutoSave(
        { content: { [type]: "", [imageId]: "" } },
        {
          loadingMsg: `Removing ${label}...`,
          successMsg: `${label} removed`,
          errorMsg: `Failed to remove ${label}`,
        }
      ).catch(() => {});
    },
    [runAutoSave]
  );

  const {
    cropModalOpen,
    currentImage,
    activeType,
    handleImageUpload,
    handleCropComplete,
    closeCropModal,
    getAspectRatio,
  } = useImageUpload(agency, imageSetters, handleImageUploadSuccess);

  const [ghlGalleryOpen, setGhlGalleryOpen] = useState(false);
  const [ghlImageType, setGhlImageType] = useState("");

  const cropAspectByContentType = {
    main_image: 2.74,
    img_1: 2,
    optin_image: 2.5,
  };

  const openGhlGallery = (type, imageId) => {
    setGhlImageType(type);
    setGhlGalleryOpen(true);
  };

  const handleGhlImageSelect = (pick) => {
    if (!pick?.url || !ghlImageType) return;
    if (ghlImageType === "main_image") {
      setMainImage(pick?.url);
      setMainImageId(pick?._id);
      setPreviewMainImage(pick?.url);
    } else if (ghlImageType === "img_1") {
      setImg1(pick?.url);
      setImg1Id(pick?._id);
      setPreviewImg1(pick?.url);
    } else if (ghlImageType === "optin_image") {
      setOptinImage(pick?.url);
      setOptinImageId(pick?._id);
      setPreviewOptinImage(pick?.url);
    } else {
      return;
    }
    handleImageUploadSuccess(ghlImageType, pick?.url, pick?._id);
  };

  const {
    acknowledgments,
    handleChange: ackHandleChange,
    handleChangeDnD: ackHandleChangeDnD,
    handleAdd: ackHandleAdd,
    handleRemove: ackHandleRemove,
  } = useAcknowledgments(contentData);

  const persistAcknowledgments = useCallback(
    (list) => {
      runAutoSave(
        { content: { acknowledgments: list } },
        {
          loadingMsg: "Updating Acknowledgments...",
          successMsg: "Acknowledgments updated",
          errorMsg: "Failed to update Acknowledgments",
        }
      ).catch(() => {});
    },
    [runAutoSave]
  );

  // Wrap acknowledgment mutations so checkbox/tag changes save immediately;
  // text edits save on blur via a separate path (see AcknowledgmentContentSection).
  const handleAckChange = useCallback(
    (index, field, value, { autoSave = true } = {}) => {
      ackHandleChange(index, field, value);
      if (autoSave) {
        const updated = acknowledgments.map((ack, i) =>
          i === index ? { ...ack, [field]: value } : ack
        );
        persistAcknowledgments(updated);
      }
    },
    [ackHandleChange, acknowledgments, persistAcknowledgments]
  );

  const handleAckChangeDnD = useCallback(
    (index, type, value) => {
      ackHandleChangeDnD(index, type, value);
      const updated = acknowledgments.map((ack, i) =>
        i === index
          ? { ...ack, dnd: { ...(ack.dnd || {}), [type]: value } }
          : ack
      );
      persistAcknowledgments(updated);
    },
    [ackHandleChangeDnD, acknowledgments, persistAcknowledgments]
  );

  const handleAckAdd = useCallback(() => {
    ackHandleAdd();
    // Optimistically build the new list to persist (matches default in hook)
    const newRow = {
      text: "I acknowledge that I am the owner or authorized representative of this business and have the authority to claim this business.",
      required: false,
      registration: false,
      newBusiness: false,
      dnd: { email: false, sms: false, call: false },
      tag: "",
    };
    persistAcknowledgments([...acknowledgments, newRow]);
  }, [ackHandleAdd, acknowledgments, persistAcknowledgments]);

  const handleAckRemove = useCallback(
    (index) => {
      ackHandleRemove(index);
      const updated = acknowledgments.filter((_, i) => i !== index);
      persistAcknowledgments(updated);
    },
    [ackHandleRemove, acknowledgments, persistAcknowledgments]
  );

  const {
    openDropdownIndex,
    tagsData,
    loadingTag,
    tagInputs,
    handleToggle,
    handleSelectTag: handleSelectTagBase,
    handleInputChange: handleInputChangeBase,
  } = useTagDropdown(services, agency);

  const handleToggleChange = () => {
    const newValue = !showMap;
    setShowMap(newValue);
    runAutoSave(
      { content: { showMap: newValue } },
      {
        loadingMsg: newValue ? "Enabling Display Map..." : "Disabling Display Map...",
        successMsg: newValue ? "Display Map enabled" : "Display Map disabled",
        errorMsg: "Failed to update Display Map",
      }
    ).catch(() => {});
  };

  const handleShowMapPinsChange = () => {
    const newValue = !showMapPins;
    setShowMapPins(newValue);
    runAutoSave(
      { content: { showMapPins: newValue } },
      {
        loadingMsg: newValue ? "Enabling map business pins..." : "Disabling map business pins...",
        successMsg: newValue ? "Map business pins enabled" : "Map business pins disabled",
        errorMsg: "Failed to update map business pins",
      }
    ).catch(() => {});
  };

  const handleShowPremiumListingBadgeChange = () => {
    const newValue = !showPremiumListingBadge;
    setShowPremiumListingBadge(newValue);
    runAutoSave(
      { content: { showPremiumListingBadge: newValue } },
      {
        loadingMsg: newValue
          ? "Enabling featured listing badge..."
          : "Disabling featured listing badge...",
        successMsg: newValue
          ? "Featured listing badge enabled"
          : "Featured listing badge disabled",
        errorMsg: "Failed to update featured listing badge",
      }
    ).catch(() => {});
  };

  const handleShowClaimedListingBadgeChange = () => {
    const newValue = !showClaimedListingBadge;
    setShowClaimedListingBadge(newValue);
    runAutoSave(
      { content: { showClaimedListingBadge: newValue } },
      {
        loadingMsg: newValue
          ? "Enabling claimed listing badge..."
          : "Disabling claimed listing badge...",
        successMsg: newValue
          ? "Claimed listing badge enabled"
          : "Claimed listing badge disabled",
        errorMsg: "Failed to update claimed listing badge",
      }
    ).catch(() => {});
  };

  const handleShowLoginChange = () => {
    const newValue = !showLogin;
    setShowLogin(newValue);
    handelThemeData({ content: { showLogin: newValue } });
    toast.success(newValue ? "Login link turned on" : "Login link turned off");
  };

  const handleShowRegisterChange = () => {
    const newValue = !showRegister;
    setShowRegister(newValue);
    handelThemeData({ content: { showRegister: newValue } });
    toast.success(newValue ? "Register link turned on" : "Register link turned off");
  };

  const handleShowAddNewBusinessChange = () => {
    const newValue = !showAddNewBusiness;
    setShowAddNewBusiness(newValue);
    handelThemeData({ content: { showAddNewBusiness: newValue } });
    toast.success(newValue ? "Add New Business link turned on" : "Add New Business link turned off");
  };

  const handleShowFollowChange = () => {
    const newValue = !showFollow;
    setShowFollow(newValue);
    handelThemeData({ content: { showFollow: newValue } });
    toast.success(newValue ? "Follow link turned on" : "Follow link turned off");
  };

  const handleShowClaimChange = () => {
    const newValue = !showClaim;
    setShowClaim(newValue);
    handelThemeData({ content: { showClaim: newValue } });
    toast.success(newValue ? "Claim link turned on" : "Claim link turned off");
  };

  const handleMapPinLimitSaved = useCallback(
    (limit) =>
      runAutoSave(
        { content: { homepageMapPinLimit: limit } },
        {
          loadingMsg: "Syncing map pin limit…",
          successMsg: "Map pin limit synced",
          errorMsg: "Failed to sync map pin limit in theme",
        }
      ),
    [runAutoSave]
  );

  const handleSelectTag = (index, tag) => {
    handleSelectTagBase(index, tag, (i, field, value) =>
      handleAckChange(i, field, value, { autoSave: true })
    );
  };

  const handleInputChange = (index, value) => {
    // Only update local input state during typing; save happens on blur via dropdown's input
    handleInputChangeBase(index, value, (i, field, val) =>
      handleAckChange(i, field, val, { autoSave: false })
    );
  };

  const handleTagBlur = (index, value) => {
    handleAckChange(index, "tag", value, { autoSave: true });
  };

  const handleAckTextBlur = (index, value) => {
    handleAckChange(index, "text", value, { autoSave: true });
  };

  return (
    <div className="sd-settings-branding-tab">
      <SoftBox>
        <Grid container spacing={3} rowSpacing={2} paddingX={3} paddingBottom={4}>
          {/* Header */}
          <HeaderContentSection
            headerEmail={headerEmail}
            setHeaderEmail={setHeaderEmail}
            headerPhone={headerPhone}
            setHeaderPhone={setHeaderPhone}
            autoSaveField={autoSaveField}
          />

          {/* Hero Section */}
          <HeroContentSection
            inputData={inputData}
            showMap={showMap}
            showMapPins={showMapPins}
            agencyId={agency?._id}
            onMapPinLimitSaved={handleMapPinLimitSaved}
            handleToggleChange={handleToggleChange}
            handleShowMapPinsChange={handleShowMapPinsChange}
            main_image={main_image}
            previewMainImage={previewMainImage}
            handleImageUpload={handleImageUpload}
            onRemoveMainImage={() => handleRemoveContentImage("main_image","main_image_id")}
            onGhlPick={() => openGhlGallery("main_image")}
            autoSaveField={autoSaveField}
          />

          {/* Homepage Optin */}
          <HomepageOptinSection
            inputData={inputData}
            optin_image={optin_image}
            previewOptinImage={previewOptinImage}
            handleImageUpload={handleImageUpload}
            onRemoveOptinImage={() => handleRemoveContentImage("optin_image", "optin_image_id")}
            onGhlPick={() => openGhlGallery("optin_image")}
            autoSaveField={autoSaveField}
          />

          {/* Home Page Main Headings Content */}
          <HomePageHeadingsSection
            inputData={inputData}
            elements={selectedTheme?.theme_data?.elements}
            img_1={img_1}
            previewImg1={previewImg1}
            handleImageUpload={handleImageUpload}
            onRemoveImg1={() => handleRemoveContentImage("img_1","img_1_id")}
            onGhlPick={() => openGhlGallery("img_1")}
            autoSaveField={autoSaveField}
            autoSaveElement={autoSaveElement}
          />

          {/* Placeholder Card (Left) */}
          <PlaceholderCard
            inputData={inputData}
            autoSaveField={autoSaveField}
            showPremiumListingBadge={showPremiumListingBadge}
            showClaimedListingBadge={showClaimedListingBadge}
            handleShowPremiumListingBadgeChange={handleShowPremiumListingBadgeChange}
            handleShowClaimedListingBadgeChange={handleShowClaimedListingBadgeChange}
          />

          {/* Home Page Main Headings Styling (Right) */}
          <HomePageHeadingsStylingSection inputData={inputData} autoSaveField={autoSaveField} />

          {/* Social & External Links */}
          <SocialLinksSection inputData={inputData} autoSaveField={autoSaveField} />

          {/* Customer Info */}
          <CustomerInfoSection inputData={inputData} autoSaveField={autoSaveField} />

          {/* Footer Content */}
          <FooterContentSection
            inputData={inputData}
            elements={selectedTheme?.theme_data?.elements}
            autoSaveField={autoSaveField}
            autoSaveElement={autoSaveElement}
          />

          {/* Button Styling */}
          <ButtonStylingSection inputData={inputData} autoSaveField={autoSaveField} />

          {/* Footer Styling */}
          <FooterStylingSection inputData={inputData} autoSaveField={autoSaveField} />

          {/* A2P Approval */}
          <A2PApprovalSection
            showLogin={showLogin}
            showRegister={showRegister}
            showAddNewBusiness={showAddNewBusiness}
            showFollow={showFollow}
            showClaim={showClaim}
            handleShowLoginChange={handleShowLoginChange}
            handleShowRegisterChange={handleShowRegisterChange}
            handleShowAddNewBusinessChange={handleShowAddNewBusinessChange}
            handleShowFollowChange={handleShowFollowChange}
            handleShowClaimChange={handleShowClaimChange}
          />

          {/* Acknowledgment Content */}
          <AcknowledgmentContentSection
            acknowledgments={acknowledgments}
            handleChange={handleAckChange}
            handleChangeDnD={handleAckChangeDnD}
            handleAdd={handleAckAdd}
            handleRemove={handleAckRemove}
            openDropdownIndex={openDropdownIndex}
            handleToggle={handleToggle}
            handleSelectTag={handleSelectTag}
            handleInputChange={handleInputChange}
            handleTagBlur={handleTagBlur}
            handleAckTextBlur={handleAckTextBlur}
            tagInputs={tagInputs}
            tagsData={tagsData}
            loadingTag={loadingTag}
          />
        </Grid>
      </SoftBox>

      <ReactCropModal
        open={cropModalOpen}
        onClose={closeCropModal}
        currentImage={currentImage}
        onCropComplete={handleCropComplete}
        aspectRatio={getAspectRatio()}
      />

      <GhlMediaGallery
        open={ghlGalleryOpen}
        onClose={() => setGhlGalleryOpen(false)}
        onSelect={handleGhlImageSelect}
        title={`Select ${labelFor(ghlImageType) || "Image"}`}
        enableCrop
        cropAspectRatio={cropAspectByContentType[ghlImageType] ?? null}
      />
    </div>
  );
}

export default Content;
